import { useDebounce } from 'custom_hooks/useDebounce';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { useCallback } from 'react';
import { countIf } from 'utils/reduction';

export type EntitySelection = Readonly<{
    corporationIds: ReadonlySet<string>;
    locationIds: ReadonlySet<string>;
}>;

type EntityState = EntitySelection &
    Readonly<{
        /** Whether the selections have been updated off of the user's favorites */
        isCustomized: boolean;
    }>;

type OrgHierarchy = Readonly<{
    /** corpId -> its location IDs */
    corpToLocations: ReadonlyMap<string, readonly string[]>;
    /** locationId -> its parent corpId */
    locationToCorp: ReadonlyMap<string, string>;
}>;

const EMPTY_SET: ReadonlySet<string> = new Set<string>();

const entityStateAtom = atom<EntityState>({
    corporationIds: EMPTY_SET,
    locationIds: EMPTY_SET,
    isCustomized: false,
});

const orgHierarchyAtom = atom<OrgHierarchy>({
    corpToLocations: new Map(),
    locationToCorp: new Map(),
});

/** see `useIsReady()` */
const isReadyAtom = atom(false);

const corporationIdsAtom = atom((get) => get(entityStateAtom).corporationIds);
const locationIdsAtom = atom((get) => get(entityStateAtom).locationIds);
const isCustomizedAtom = atom((get) => get(entityStateAtom).isCustomized);

// ---

function expandCorpChildren(
    selection: EntitySelection,
    orgs: OrgHierarchy,
): EntitySelection {
    const { corpToLocations } = orgs;
    if (corpToLocations.size === 0) {
        return selection;
    }

    let locationIds: Set<string> | undefined;
    for (const corpId of selection.corporationIds) {
        const childIds = corpToLocations.get(corpId);
        if (!childIds) {
            continue;
        }
        for (const locId of childIds) {
            if (!selection.locationIds.has(locId)) {
                locationIds ??= new Set(selection.locationIds);
                locationIds.add(locId);
            }
        }
    }
    return locationIds ? { ...selection, locationIds } : selection;
}

/** Selects each corp whose (non-empty) set of locations is entirely selected */
function promoteFullCorps(
    selection: EntitySelection,
    orgs: OrgHierarchy,
): EntitySelection {
    let corporationIds: Set<string> | undefined;
    for (const [corpId, childIds] of orgs.corpToLocations) {
        if (
            childIds.length > 0 &&
            !selection.corporationIds.has(corpId) &&
            childIds.every((locId) => selection.locationIds.has(locId))
        ) {
            corporationIds ??= new Set(selection.corporationIds);
            corporationIds.add(corpId);
        }
    }
    return corporationIds ? { ...selection, corporationIds } : selection;
}

/**
 * Makes a selection consistent with the org hierarchy: a selected corp has all
 * its locations selected, and a corp with all its locations selected is
 * itself selected
 */
function reconcileWithOrgs(
    selection: EntitySelection,
    orgs: OrgHierarchy,
): EntitySelection {
    return promoteFullCorps(expandCorpChildren(selection, orgs), orgs);
}

const setCorporationsAtom = atom(
    null,
    (
        get,
        set,
        corporations: readonly {
            Id: string;
            Locations: readonly { LocationId: string }[];
        }[],
    ) => {
        const corpToLocations = new Map<string, readonly string[]>();
        const locationToCorp = new Map<string, string>();
        for (const corp of corporations) {
            const locIds = corp.Locations.map((l) => l.LocationId);
            corpToLocations.set(corp.Id, locIds);
            for (const locId of locIds) {
                locationToCorp.set(locId, corp.Id);
            }
        }
        const orgs = { corpToLocations, locationToCorp };
        set(orgHierarchyAtom, orgs);

        // Corporations may arrive incrementally (paged), so a corp selected
        // before its locations were known needs its children filled in now,
        // and a corp whose locations were all selected needs selecting.
        const prev = get(entityStateAtom);
        const reconciled = reconcileWithOrgs(prev, orgs);
        if (reconciled !== prev) {
            set(entityStateAtom, { ...prev, ...reconciled });
        }
    },
);

const resetToFavoritesAtom = atom(null, (get, set, favorites: EntitySelection) => {
    const orgs = get(orgHierarchyAtom);
    set(entityStateAtom, {
        ...reconcileWithOrgs(favorites, orgs),
        isCustomized: false,
    });
});

const toggleCorporationAtom = atom(null, (get, set, corpId: string) => {
    const orgs = get(orgHierarchyAtom);
    const corpLocationIds = orgs.corpToLocations.get(corpId) ?? [];
    const prev = get(entityStateAtom);
    const nextCorpIds = new Set(prev.corporationIds);
    const nextLocIds = new Set(prev.locationIds);
    if (nextCorpIds.has(corpId)) {
        nextCorpIds.delete(corpId);
        for (const locId of corpLocationIds) {
            nextLocIds.delete(locId);
        }
    } else {
        nextCorpIds.add(corpId);
        for (const locId of corpLocationIds) {
            nextLocIds.add(locId);
        }
    }
    set(entityStateAtom, {
        corporationIds: nextCorpIds,
        locationIds: nextLocIds,
        isCustomized: true,
    });
});

const toggleLocationAtom = atom(null, (get, set, locationId: string) => {
    const orgs = get(orgHierarchyAtom);
    const corpId = orgs.locationToCorp.get(locationId);
    const corpLocationIds = corpId ? (orgs.corpToLocations.get(corpId) ?? []) : [];
    const prev = get(entityStateAtom);
    let nextCorpIds = prev.corporationIds;
    const nextLocIds = new Set(prev.locationIds);
    const removing = nextLocIds.has(locationId);
    if (removing) {
        nextLocIds.delete(locationId);
        // deselecting a location means its corp no longer has all locs
        // selected, so deselect corp if necessary
        if (corpId && nextCorpIds.has(corpId)) {
            const mut = (nextCorpIds = new Set(nextCorpIds));
            mut.delete(corpId);
        }
    } else {
        nextLocIds.add(locationId);
        // auto-select the corp when all its locations are now selected
        if (
            corpId &&
            !nextCorpIds.has(corpId) &&
            corpLocationIds.every((id) => nextLocIds.has(id))
        ) {
            const mut = (nextCorpIds = new Set(nextCorpIds));
            mut.add(corpId);
        }
    }
    set(entityStateAtom, {
        corporationIds: nextCorpIds,
        locationIds: nextLocIds,
        isCustomized: true,
    });
});

const locSelectionAtom = atom((get) => {
    const { corporationIds, locationIds } = get(entityStateAtom);
    const { corpToLocations } = get(orgHierarchyAtom);

    return (corpId: string): 'none' | 'some' | 'all' => {
        if (corporationIds.has(corpId)) {
            return 'all';
        }
        const childIds = corpToLocations.get(corpId);
        if (!childIds || childIds.length === 0) {
            return 'none';
        }
        const count = countIf(childIds, (locId) => locationIds.has(locId));
        if (count === 0) {
            return 'none';
        }
        if (count === childIds.length) {
            return 'all';
        }
        return 'some';
    };
});

/**
 * The selected location IDs, minus those whose corporation is selected, since
 * the corporation's ID already covers them in a query
 */
const queryLocationIdsAtom = atom((get): ReadonlySet<string> => {
    const { corporationIds, locationIds } = get(entityStateAtom);
    const { locationToCorp } = get(orgHierarchyAtom);
    const queryLocationIds = new Set<string>();
    for (const locId of locationIds) {
        const corpId = locationToCorp.get(locId);
        if (!corpId || !corporationIds.has(corpId)) {
            queryLocationIds.add(locId);
        }
    }
    return queryLocationIds;
});

/** Corp/location IDs to scope a query to; `undefined` for none of that kind */
export type QueryScope = Readonly<{
    corporationIds: string[] | undefined;
    locationIds: string[] | undefined;
}>;

const selectionScopeAtom = atom((get): QueryScope => {
    const corporationIds = get(corporationIdsAtom);
    const locationIds = get(queryLocationIdsAtom);
    return {
        corporationIds: corporationIds.size > 0 ? [...corporationIds] : undefined,
        locationIds: locationIds.size > 0 ? [...locationIds] : undefined,
    };
});

/** how long a selection change waits before reaching `useQueryScope()` */
const SELECTION_DEBOUNCE_MS = 300;

// ---

function useCorporationIds() {
    return useAtomValue(corporationIdsAtom);
}

function useLocationIds() {
    return useAtomValue(locationIdsAtom);
}

/**
 * The selected location IDs not covered by a selected corporation, i.e.
 * `useLocationIds()` minus the locations of `useCorporationIds()`
 */
function useQueryLocationIds() {
    return useAtomValue(queryLocationIdsAtom);
}

/**
 * The IDs to scope Mission Control's queries to: the route's location if
 * given, else the route's corporation, else the selection. A query matches
 * rows in ANY of the corporations OR ANY of the locations, so the scope never
 * lists a location alongside its own corporation.
 *
 * Selection changes arrive debounced, since each one refetches the queues and
 * rerenders their lists, which is expensive enough to make checkbox clicks
 * feel sluggish. Throws when there is neither a route scope nor a selection.
 */
function useQueryScope(
    route: Readonly<{ corporationId?: string; locationId?: string }>,
): QueryScope {
    const selectionScope = useDebounce(
        useAtomValue(selectionScopeAtom),
        SELECTION_DEBOUNCE_MS,
    );
    if (route.locationId) {
        if (!route.corporationId) {
            throw new Error('had a location but no corporation');
        }
        return { corporationIds: undefined, locationIds: [route.locationId] };
    }
    if (route.corporationId) {
        return { corporationIds: [route.corporationId], locationIds: undefined };
    }
    if (!selectionScope.corporationIds && !selectionScope.locationIds) {
        throw new Error('had no route scope and no selected corporations or locations');
    }
    return selectionScope;
}

function useIsLocationSelected(locationId: string): boolean {
    const selector = useCallback(
        (state: EntityState) => state.locationIds.has(locationId),
        [locationId],
    );
    return useAtomValue(selectAtom(entityStateAtom, selector));
}

function useIsCorpSelected(corpId: string): boolean {
    const selector = useCallback(
        (state: EntityState) => state.corporationIds.has(corpId),
        [corpId],
    );
    return useAtomValue(selectAtom(entityStateAtom, selector));
}

/**
 * Whether the starting selection has been settled (from the user's favorites,
 * if they have any), so that an empty selection is the user's own
 */
function useIsReady() {
    return useAtomValue(isReadyAtom);
}

const initializeAtom = atom(null, (get, set, favorites: EntitySelection) => {
    if (get(isReadyAtom)) {
        return;
    }
    if (favorites.corporationIds.size > 0 || favorites.locationIds.size > 0) {
        set(resetToFavoritesAtom, favorites);
    }
    set(isReadyAtom, true);
});

/**
 * Settles the starting selection as the user's favorites, once the
 * corporations are known. Only the first call has any effect.
 */
function useInitialize() {
    return useSetAtom(initializeAtom);
}

const syncFavoritesAtom = atom(null, (get, set, favorites: EntitySelection) => {
    if (get(isReadyAtom) && !get(isCustomizedAtom)) {
        set(resetToFavoritesAtom, favorites);
    }
});

/**
 * Keeps an uncustomized selection in step with the user's favorites as they
 * change (e.g. a favorite removed elsewhere in the app). Has no effect before
 * `useInitialize()` settles the starting selection, or once the user has
 * customized it.
 */
function useSyncFavorites() {
    return useSetAtom(syncFavoritesAtom);
}

function useLocSelection() {
    return useAtomValue(locSelectionAtom);
}

function useIsCustomized() {
    return useAtomValue(isCustomizedAtom);
}

function useSetCorporations() {
    return useSetAtom(setCorporationsAtom);
}

function useResetToFavorites() {
    return useSetAtom(resetToFavoritesAtom);
}

function useToggleCorporation() {
    return useSetAtom(toggleCorporationAtom);
}

function useToggleLocation() {
    return useSetAtom(toggleLocationAtom);
}

const setEntitySelectionAtom = atom(null, (get, set, selection: EntitySelection) => {
    const orgs = get(orgHierarchyAtom);
    set(entityStateAtom, {
        ...reconcileWithOrgs(selection, orgs),
        isCustomized: true,
    });
});

function useSetEntitySelection() {
    return useSetAtom(setEntitySelectionAtom);
}

const clearSelectionAtom = atom(null, (_get, set) => {
    set(entityStateAtom, {
        corporationIds: EMPTY_SET,
        locationIds: EMPTY_SET,
        isCustomized: true,
    });
});

/** Deselects every corporation and location, without restoring favorites */
function useClearSelection() {
    return useSetAtom(clearSelectionAtom);
}

export const MissionControlOrgs = {
    locationIdsAtom,
    useCorporationIds,
    useLocationIds,
    useQueryLocationIds,
    useQueryScope,
    useIsLocationSelected,
    useIsCorpSelected,
    useLocSelection,
    useIsCustomized,
    useIsReady,
    useInitialize,
    useSyncFavorites,
    useSetCorporations,
    useResetToFavorites,
    useToggleCorporation,
    useToggleLocation,
    useSetEntitySelection,
    useClearSelection,
};
